import { inject, Service, signal } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable } from "rxjs";

import { AdaaHelper } from "../../core/utils";
import { Constants } from "../constants/constants";
import { GovDirection, ValueText } from "../models";
import {
  EntitiesApiService,
  GovDirectionApiService,
  PropertiesService,
  UserGroupsApiService,
  UsersApiService,
  ValidatorApiService,
} from ".";

@Service()
export class GovernmentDirectionsEditorService {
  private _formBuilder = inject(FormBuilder);
  private _translateService = inject(TranslateService);
  private _validatorApiService = inject(ValidatorApiService);
  private _govDirectionApiService = inject(GovDirectionApiService);
  private _entitiesApiService = inject(EntitiesApiService);
  private _usersApiService = inject(UsersApiService);
  private _userGroupsApiService = inject(UserGroupsApiService);
  private _propsService = inject(PropertiesService);
  private _toastrService = inject(ToastrService);
  private _router = inject(Router);

  entities = signal<ValueText[]>([]);
  owners = signal<ValueText[]>([]);
  strategyOwnerGroups = signal<ValueText[]>([]);
  subStrategyOwnerGroups = signal<ValueText[]>([]);
  nationalStrategyTypes = signal<ValueText[]>([]);

  public initPage(): void {
    this._getEntities();
    this._getOwners();
    this._getStrategyOwnerGroup();
    this._getNationalStrategyTypes();
  }

  public getDirection(directionId: number) {
    return new Observable<GovDirection | undefined>((subscriber) => {
      this._govDirectionApiService.getDirection(directionId).subscribe({
        next: (response) => {
          if (response.inError) subscriber.next(undefined);
          subscriber.next(response.responseData);
        },
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public saveDirection(directionId: number | undefined, data: any) {
    this._govDirectionApiService.saveDirection(directionId ? "update" : "create", data).subscribe({
      next: (response) => {
        if (response.inError) return;

        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigate(["/console/directions"]);
      },
    });
  }

  private _getEntities() {
    this._entitiesApiService.getAllV2().subscribe({
      next: (response) => {
        if (response.inError) return;
        this.entities.set(
          AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"))
        );
      },
    });
  }

  private _getOwners() {
    this._usersApiService.getOwnersPerEntity(Constants.CONSTANT_PMO_ID).subscribe({
      next: (response) => {
        if (response.inError) return;

        const ddl = AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"));
        this._translateService.get("common.form.label.none").subscribe({
          next: (translatedTitle) => {
            ddl.unshift({ value: 0, text: translatedTitle });
            this.owners.set(ddl);
          },
        });
      },
    });
  }

  private _getStrategyOwnerGroup() {
    this._userGroupsApiService.getall(Constants.CONSTANT_PMO_ID).subscribe({
      next: (response) => {
        if (response.inError) return;

        this.subStrategyOwnerGroups.set(
          AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"))
        );

        const ddl = AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"));
        this._translateService.get("common.form.label.none").subscribe({
          next: (translatedTitle) => {
            ddl.unshift({ value: 0, text: translatedTitle });
            this.strategyOwnerGroups.set(ddl);
          },
        });
      },
    });
  }

  private _getNationalStrategyTypes() {
    this._propsService.getPropById(Constants.CONSTANT_NATIONAL_STRATEGY_TYPES).subscribe({
      next: (response) => {
        if (response.inError) return;
        this.nationalStrategyTypes.set(
          AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"))
        );
      },
    });
  }
}
